import { customElement, property } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-chip.css';

export type ExtChipKind = 'neutral' | 'success' | 'danger' | 'warning' | 'info';
export type ExtChipSize = 'sm' | 'md';

/**
 * <ext-chip kind="success" size="md" icon="check" removable>Activo</ext-chip>
 *
 * Badge/tag con variantes de estado. Light DOM: el contenido textual es child
 * directo del <ext-chip> (no hay <slot>). Si se pasa `icon`, se prepende un
 * <ext-icon>. Si `removable`, se apende un boton <button> con una "x" y un
 * click dispara el CustomEvent "ext:remove".
 *
 * Props:
 *  - kind: neutral | success | danger | warning | info (default neutral).
 *  - size: sm | md (default md).
 *  - removable: boolean (default false) - muestra x para remover.
 *  - icon: string opcional, nombre lucide a renderear antes del texto.
 *
 * Eventos:
 *  - ext:remove (detail vacio, bubbles + composed) al clickear la x.
 */
@customElement('ext-chip')
export class ExtChip extends ExtBaseElement {
  @property({ type: String, reflect: true }) kind: ExtChipKind = 'neutral';
  @property({ type: String, reflect: true }) size: ExtChipSize = 'md';
  @property({ type: Boolean, reflect: true }) removable = false;
  @property({ type: String }) icon = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  render(): null {
    return null;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._syncIcon();
    this._syncRemove();
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('icon')) this._syncIcon();
    if (changed.has('removable')) this._syncRemove();
  }

  private _syncIcon(): void {
    const existing = this.querySelector(':scope > .ext-chip__icon');
    if (existing) existing.remove();
    if (!this.icon) return;
    const ic = document.createElement('ext-icon');
    ic.classList.add('ext-chip__icon');
    ic.setAttribute('name', this.icon);
    ic.setAttribute('size', 'sm');
    ic.setAttribute('aria-hidden', 'true');
    this.insertBefore(ic, this.firstChild);
  }

  private _syncRemove(): void {
    const existing = this.querySelector(':scope > .ext-chip__remove');
    if (existing) existing.remove();
    if (!this.removable) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ext-chip__remove';
    btn.setAttribute('aria-label', 'Remover');
    btn.textContent = '\u00D7'; // ×
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      this._emit('remove');
    });
    this.appendChild(btn);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-chip': ExtChip;
  }
}
